import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import * as scanner from '../../platform/scanner';

interface Props {
  title: string;
  onRead: (value: string) => void;
  onCancel: () => void;
}

/**
 * The camera, borrowed for one reading.
 *
 * On a phone there is no gun and no COM port, so this is how a shelf label gets
 * into a field. It closes on the first reading: unlike serial numbers, where a
 * parcel is worked through one unit at a time, a place is chosen once.
 *
 * Rendered at the top of the document rather than where it is written. The
 * receiving screen puts a location field inside its own form, and a sheet nested
 * there would be clipped by the first scrolling ancestor it meets.
 */
export function ScanSheet({ title, onRead, onCancel }: Props) {
  const { t } = useTranslation();

  const video = useRef<HTMLVideoElement>(null);
  const session = useRef<scanner.Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');

  const support = scanner.cameraSupport();

  useEffect(() => {
    let cancelled = false;
    const begin = async () => {
      if (!video.current) return;
      try {
        let readBeforeStartFinished = false;
        const started = await scanner.start(video.current, (reading) => {
          // Native readers can report a code while start() is still awaiting
          // its Session. Remember it and stop the session immediately once it
          // arrives, rather than reading `started` in its temporal dead zone.
          readBeforeStartFinished = true;
          onRead(reading.value);
        });
        if (cancelled || readBeforeStartFinished) started.stop();
        else session.current = started;
      } catch (exc) {
        setError(`scan.camera.${(exc as Error).message}`);
      }
    };
    if (support === 'ok') void begin();
    // A stream left running keeps the indicator light on and the phone warm,
    // and looks to the user exactly like the application spying on them.
    return () => {
      cancelled = true;
      session.current?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [support]);

  return createPortal(
    <div className="serial-scan" role="dialog" aria-modal="true">
      <div className="serial-scan__box">
        <header className="serial-scan__head">
          <h3>{title}</h3>
          <button type="button" onClick={onCancel}>
            {t('common.close')}
          </button>
        </header>

        <div className="scan__camera">
          <video ref={video} className="scan__video is-live" muted />
          {support !== 'ok' && <p className="hint">{t(`scan.camera.${support}`)}</p>}
          {error && <p className="hint">{t(error)}</p>}
        </div>

        {/* A label that has rubbed off is exactly the case the camera cannot
            help with, so typing is never more than one field away. */}
        <form
          className="serial-scan__manual"
          onSubmit={(event) => {
            event.preventDefault();
            if (typed.trim()) onRead(typed.trim());
          }}
        >
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={t('scan.manual')}
            autoCapitalize="characters"
            spellCheck={false}
          />
          <button type="submit">{t('common.add')}</button>
        </form>
      </div>
    </div>,
    document.body,
  );
}
